// Contatori diagnostici delle operazioni di telemetria. Specchio di
// `CloseYourIt::Stats` (gem) e `Stats` (dart). Oltre agli aggregati
// (`enqueued`/`sent`/`dropped`/`failed`, più `timeout` come sotto-conteggio dei
// `failed`, parità gem Ruby) tiene la ripartizione `reasons` per rendere
// visibile il MOTIVO di ogni scarto/fallimento — così `getStats` distingue una
// coda piena da un drop di `beforeSend` o da un 5xx (CYJS-9).

import type { DropReason, StatsSnapshot } from './types'

export type { StatsSnapshot } from './types'

export type StatKey = 'enqueued' | 'sent' | 'dropped' | 'failed' | 'timeout'

const DROP_REASONS: DropReason[] = [
  'sample_rate',
  'before_send',
  'error',
  'queue_full',
  'response',
  'network',
]

// Motivi che rappresentano un invio TENTATO ma fallito (non-2xx / rete): contano
// tra i `failed`. Gli altri sono scarti client-side (mai partiti) → `dropped`.
const FAILED_REASONS = new Set<DropReason>(['response', 'network'])

/// Snapshot con tutti i contatori a zero (usato pre-init / dopo close).
export function emptyStatsSnapshot(): StatsSnapshot {
  return {
    enqueued: 0,
    sent: 0,
    dropped: 0,
    failed: 0,
    timeout: 0,
    reasons: zeroReasons(),
  }
}

function zeroReasons(): Record<DropReason, number> {
  return {
    sample_rate: 0,
    before_send: 0,
    error: 0,
    queue_full: 0,
    response: 0,
    network: 0,
  }
}

export class Stats {
  private counts: Record<StatKey, number> = {
    enqueued: 0,
    sent: 0,
    dropped: 0,
    failed: 0,
    timeout: 0,
  }

  private reasons: Record<DropReason, number> = zeroReasons()

  /// Incrementa un contatore aggregato ([enqueued]/[sent] nell'uso normale).
  increment(key: StatKey): void {
    this.counts[key] += 1
  }

  /// Valore corrente di un contatore aggregato.
  get(key: StatKey): number {
    return this.counts[key]
  }

  /// Registra uno scarto/fallimento con il suo [reason]: incrementa `dropped`
  /// (scarti client-side) oppure `failed` (`response`/`network`, invio tentato)
  /// e sempre il contatore del motivo in `reasons`.
  record(reason: DropReason): void {
    this.reasons[reason] += 1
    if (FAILED_REASONS.has(reason)) this.counts.failed += 1
    else this.counts.dropped += 1
  }

  /// Registra un timeout di rete: `failed` + `timeout` (sotto-conteggio dei
  /// falliti, parità gem Ruby: isola i problemi di connettività dai non-2xx).
  recordTimeout(): void {
    this.counts.failed += 1
    this.counts.timeout += 1
  }

  /// Snapshot dei contatori (copia profonda: nessun aliasing con lo stato interno).
  toObject(): StatsSnapshot {
    return { ...this.counts, reasons: { ...this.reasons } }
  }

  /// Azzera tutti i contatori e i motivi.
  reset(): void {
    for (const key of Object.keys(this.counts) as StatKey[]) this.counts[key] = 0
    for (const reason of DROP_REASONS) this.reasons[reason] = 0
  }
}
